import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { API } from '@/config/api';

export const useTimeEntries = (userId = null, startDate = null, endDate = null) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (userId) params.append('user_id', userId);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const response = await axios.get(`${API}/time-entries?${params.toString()}`);
      setEntries(Array.isArray(response.data) ? response.data : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching entries:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, startDate, endDate]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const createEntry = async (entryData) => {
    try {
      const response = await axios.post(`${API}/time-entries`, entryData);
      await fetchEntries();
      return { success: true, entry: response.data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const deleteEntry = async (entryId) => {
    try {
      await axios.delete(`${API}/time-entries/${entryId}`);
      await fetchEntries();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  return { entries, loading, error, refetch: fetchEntries, createEntry, deleteEntry };
};

export const useMonthlyEntries = (year, month, userId = null) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API}/time-entries/month/${year}/${month}`;
      if (userId) url += `?user_id=${userId}`;
      const response = await axios.get(url);
      setEntries(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching monthly entries:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [year, month, userId]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  return { entries, loading, error, refetch: fetchEntries };
};
